import { Observable } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { CookieService } from '@coin-depo-core/storage';

import type {
  BaseApiRequest,
  ApiResponse,
  ExtendedApiResponse,
  SupportedCoinsRequest,
  AccountStatsRequest,
  AccountStatsResponse,
  BalancesRequest,
  BalancesResponse,
  CreateDepositAccountRequest,
  DepositAssetsRequest,
  DepositAssetsResponse,
  SupportedCoinsResponse,
  SwapToken,
  TokenStats,
  SupportedTokens,
  NewDepositAssetsResponse,
  UserWalletRequest,
  TransactionsRequest,
  TransactionsResponse,
  CountriesResponse,
  SourceOfFoundsResponse,
  SupportedDocumentsRequest,
  SupportedDocumentsResponse,
  BorrowUserRequest,
  RepayUserLoanRequest,
  UserLoanRequest,
  BorrowUserResponse,
  LoanInfoResponce,
  GetAdsResponse,
  GovernanceRequest,
  GovernanceResponse,
  GovernanceVoteRequest,
  GovernanceVotingHistoryRequest,
} from '@coin-depo-types';

import { ServerNotRespondingService } from '@coin-depo-core/server-not-responding.service';
import { StorageService } from '@coin-depo-core/storage';
import { BaseApiService } from './base.service';


@Injectable({
  providedIn: 'root',
})
export class ApiService extends BaseApiService {
  override serviceName = 'ApiService';

  constructor(
    protected override http: HttpClient,
    protected override cookies: CookieService,
    protected override router: Router,
    protected override storage: StorageService,
    protected override serverNotRespondingService: ServerNotRespondingService
  ) {
    super();
  }

  getAccountStats(
    data: AccountStatsRequest
  ): Observable<ExtendedApiResponse<AccountStatsResponse>> {
    return this.processRequest<
      AccountStatsRequest,
      ExtendedApiResponse<AccountStatsResponse>
    >('accountStats', data);
  }

  getBalances(
    data: BalancesRequest
  ): Observable<ExtendedApiResponse<BalancesResponse>> {
    return this.processRequest<
      BalancesRequest,
      ExtendedApiResponse<BalancesResponse>
    >('balances', data);
  }

  getSupportedCoins(
    data: SupportedCoinsRequest
  ): Observable<ExtendedApiResponse<SupportedCoinsResponse>> {
    return this.processRequest<
      SupportedCoinsRequest,
      ExtendedApiResponse<SupportedCoinsResponse>
    >('supportedCoins', data);
  }

  getDepositAssets(
    data: DepositAssetsRequest
  ): Observable<ExtendedApiResponse<DepositAssetsResponse>> {
    return this.processRequest<
      DepositAssetsRequest,
      ExtendedApiResponse<DepositAssetsResponse>
    >('assetDropdown', data);
  }

  getCountries(
    data: BaseApiRequest & any
  ): Observable<ExtendedApiResponse<CountriesResponse>> {
    return this.processRequest<
      BaseApiRequest,
      ExtendedApiResponse<CountriesResponse>
    >('getCountries', data);
  }

  getAllCountries(
    data: BaseApiRequest & any
  ): Observable<ExtendedApiResponse<CountriesResponse>> {
    return this.processRequest<
      BaseApiRequest,
      ExtendedApiResponse<CountriesResponse>
    >('getAllCountries', data);
  }

  getSourceOfFunds(
    data: BaseApiRequest
  ): Observable<ExtendedApiResponse<SourceOfFoundsResponse>> {
    return this.processRequest<
      BaseApiRequest,
      ExtendedApiResponse<SourceOfFoundsResponse>
    >('getSourceOfFunds', data);
  }

  getTransactionsList(
    data: TransactionsRequest
  ): Observable<ExtendedApiResponse<TransactionsResponse>> {
    return this.processRequest<
      TransactionsRequest,
      ExtendedApiResponse<TransactionsResponse>
    >('transactionsList', data);
  }

  getTransactionsCsvList(data: any): Observable<any> {
    return this.processBlobRequest<any>('transactionsList', data);
  }

  cancelWithdrawal(data: any): Observable<ExtendedApiResponse<any>> {
    return this.processRequest<any, ExtendedApiResponse<any>>(
      'cancelWithdrawal',
      data
    );
  }

  getUserWallet(data: UserWalletRequest): Observable<ExtendedApiResponse<any>> {
    return this.processRequest<UserWalletRequest, ExtendedApiResponse<any>>(
      'userWallet',
      data
    );
  }

  withdrawFundsRequest(data: any): Observable<ExtendedApiResponse<any>> {
    return this.processRequest<any, ExtendedApiResponse<any>>(
      'withdrawFundsRequest',
      data
    );
  }

  withdrawFundsConfirmation(data: any): Observable<ExtendedApiResponse<any>> {
    return this.processRequest<any, ExtendedApiResponse<any>>(
      'withdrawFundsConfirmation',
      data
    );
  }

  internalTransfer(data: any): Observable<ExtendedApiResponse<any>> {
    return this.processRequest<any, ExtendedApiResponse<any>>(
      'internalTransfer',
      data
    );
  }

  createDepositPlan(data: any): Observable<ExtendedApiResponse<any>> {
    return this.processRequest<any, ExtendedApiResponse<any>>(
      'createDepositPlan',
      data
    );
  }

  resendWithdrawalCode(data: any): Observable<ExtendedApiResponse<any>> {
    return this.processRequest<any, ExtendedApiResponse<any>>(
      'resendWithdrawalCode',
      data
    );
  }

  getNewDepositAssets(
    data: BaseApiRequest
  ): Observable<ExtendedApiResponse<NewDepositAssetsResponse>> {
    return this.processRequest<
      BaseApiRequest,
      ExtendedApiResponse<NewDepositAssetsResponse>
    >('newAssetDropdown', data);
  }

  setAccountVisibility(
    data: CreateDepositAccountRequest
  ): Observable<ApiResponse> {
    return this.processRequest<CreateDepositAccountRequest, ApiResponse>(
      'accountVisibility',
      data
    );
  }

  getSupportedDocuments(data: SupportedDocumentsRequest) {
    return this.processRequest<
      SupportedDocumentsRequest,
      ExtendedApiResponse<SupportedDocumentsResponse>
    >('supportedDocuments', data);
  }

  getTokenStats(param: BaseApiRequest) {
    return this.processRequest<BaseApiRequest, ApiResponse & TokenStats>(
      'tokenStats',
      param
    );
  }

  getSupportedTokens(param: BaseApiRequest) {
    return this.processRequest<BaseApiRequest, ApiResponse & SupportedTokens>(
      'supportedTokens',
      param
    );
  }

  tokenSwap(param: BaseApiRequest & SwapToken) {
    return this.processRequest<BaseApiRequest, ApiResponse>('tokenSwap', param);
  }

  userInfoBorrow(param: BorrowUserRequest) {
    return this.processRequest<BorrowUserRequest, ExtendedApiResponse<BorrowUserResponse>>(
      'getUserBorrow',
      param
    )
  }

  getUserLoan(data: UserLoanRequest) {
    return this.processRequest<UserLoanRequest, ExtendedApiResponse<any>>(
      'getUserLoan',
      data
    );
  }

  repayUserLoan(data: RepayUserLoanRequest) {
    return this.processRequest<RepayUserLoanRequest, ApiResponse>(
      'repayUserLoan',
      data
    );
  }

  
  getLoanStats(data: BorrowUserRequest): Observable<ExtendedApiResponse<LoanInfoResponce>> {
    return this.processRequest<BorrowUserRequest, ExtendedApiResponse<LoanInfoResponce>>(
      'getLoanStats',
      data
    );
  }

  getAds(data: BaseApiRequest): Observable<ExtendedApiResponse<GetAdsResponse>> {
    return this.processRequest<BaseApiRequest, ExtendedApiResponse<GetAdsResponse>>
    ('getAds', data)
  }

  getVestingCsv(data: any): Observable<any> {
    return this.processBlobRequest<any>('vestingList', data);
  }

  setPayoutCurrency(data: any): Observable<ExtendedApiResponse<any>> {
    return this.processRequest<BorrowUserRequest, ExtendedApiResponse<any>>(
      'setPayoutCurrency',
      data
    );
  }

  getGovernanceGeneral(data: GovernanceRequest): Observable<ExtendedApiResponse<GovernanceResponse>> {
    return this.processRequest<GovernanceRequest, ExtendedApiResponse<GovernanceResponse>>(
      'governance',
      data
    );
  }

  setGovernanceVote(data: GovernanceVoteRequest): Observable<ExtendedApiResponse<GovernanceResponse>> {
    return this.processRequest<GovernanceVoteRequest, ExtendedApiResponse<GovernanceResponse>>(
      'setGovernanceVote',
      data
    );
  }

  getGovernanceVotingHistory(data: GovernanceVotingHistoryRequest): Observable<ExtendedApiResponse<any>> {
    return this.processRequest<GovernanceVotingHistoryRequest, ExtendedApiResponse<any>>(
      'governanceVotingHistory',
      data
    );
  }

  getHistoricalTokenBalance(data: any): Observable<ExtendedApiResponse<any>> {
    return this.processRequest<any, ExtendedApiResponse<any>>(
      'historicalTokenBalance',
      data
    );
  }
}